<script setup>
import Select from './Components/Form/Select.vue';
import Button from './Components/Button.vue';
import { Head, useForm } from "@inertiajs/vue3";
import { ref, watch, onMounted } from "vue";
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/vue'
import Checkbox from './Components/Form/Checkbox.vue';
import Vehicle from './Components/Vehicle.vue';

const props = defineProps({
    vehicles: Array
})

const minYear = Math.min(...props.vehicles.map(vehicle => vehicle.year));
const maxYear = Math.max(...props.vehicles.map(vehicle => vehicle.year));

const sortOptions = ref([
    { label: "Nieuwste eerst", id: "newest" },
    { label: "Oudste eerst", id: "oldest" },
    { label: "Prijs laag naar hoog", id: "prise-asc" },
    { label: "prijs van hoog naar laag", id: "prise-desc" },
]);

const selectedSort = ref("newest");

const mobileFiltersOpen = ref(false);

const vehicles = ref(props.vehicles);
const loadedVehicles = ref(10);

const filteredVehicles = ref([]);

function sort() {
    if (selectedSort.value === "newest") {
        filteredVehicles.value.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    } else if (selectedSort.value === "oldest") {
        filteredVehicles.value.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    } else if (selectedSort.value === "prise-asc") {
        filteredVehicles.value.sort((a, b) => a.price - b.price);
    } else if (selectedSort.value === "prise-desc") {
        filteredVehicles.value.sort((a, b) => b.price - a.price);
    }
    vehicles.value = filteredVehicles.value;
    loadedVehicles.value = vehicles.value.length;
}

const eurFormatter = new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' });

const filterForm = useForm({
    brand: null,
    model: null,
    type: null,
    price: [null, null],
    construction_year: [null, null],
    mileage: [null, null],
    fuel: null,
    transmission: null,
    body: null,
    accessories: [],
    exterior_color: [],
    interior_color: [],
    upholstery: [],
})

//watch all form values
const brandOptions = ref([...new Set(props.vehicles.map(vehicle => vehicle.brand))].map(brand => ({ id: brand, label: brand })));

const modelOptions = ref([]);
watch(
    () => filterForm.brand,
    (newBrand) => {
        if (newBrand) {
            modelOptions.value = [...new Set(
                props.vehicles
                    .filter(vehicle => vehicle.brand === newBrand)
                    .map(vehicle => vehicle.model)
            )].map(model => ({ id: model, label: model }));
        } else {
            modelOptions.value = [{ id: null, label: 'Geen resultaten gevonden' }];
            typeOptions.value = [{ id: null, label: 'Geen resultaten gevonden' }];
        }

        //when brand has changed, model should be reset
        filterForm.model = null;
        filterForm.type = null;
    }
);

const typeOptions = ref([]);
watch(
    () => filterForm.model,
    (newModel) => {
        if (newModel) {
            typeOptions.value = [...new Set(
                props.vehicles
                    .filter(vehicle => vehicle.brand === filterForm.brand)
                    .filter(vehicle => vehicle.model === newModel)
                    .map(vehicle => vehicle.type)
            )].map(type => ({ id: type, label: type }));
        } else {
            typeOptions.value = [{ id: null, label: 'Geen resultaten gevonden' }];
        }

        //when model has changed, type should be reset
        filterForm.type = null;
    }
);


const bodyTypes = ref([...new Set(props.vehicles.map(vehicle => vehicle.body))].map(body => ({ id: body, label: body })));
const fuelTypes = ref([...new Set(props.vehicles.map(vehicle => vehicle.fuel))].map(fuel => ({ id: fuel, label: fuel })));
const transmissionTypes = ref([...new Set(props.vehicles.map(vehicle => vehicle.transmission))].map(transmission => ({ id: transmission, label: transmission })));

watch(() => filterForm, filter, { deep: true });

const filterChanged = ref(false);

function filter() {
    //get the form values, and apply all the filters

    let filteredVehiclesList = props.vehicles;

    if (filterForm.brand) {
        filteredVehiclesList = filteredVehiclesList.filter(vehicle => vehicle.brand === filterForm.brand);
    }

    if (filterForm.model) {
        filteredVehiclesList = filteredVehiclesList.filter(vehicle => vehicle.model === filterForm.model);
    }

    if (filterForm.type) {
        filteredVehiclesList = filteredVehiclesList.filter(vehicle => vehicle.type === filterForm.type);
    }

    if (filterForm.body) {
        filteredVehiclesList = filteredVehiclesList.filter(vehicle => vehicle.body === filterForm.body);
    }

    if (filterForm.fuel) {
        filteredVehiclesList = filteredVehiclesList.filter(vehicle => vehicle.fuel === filterForm.fuel);
    }

    if (filterForm.transmission) {
        filteredVehiclesList = filteredVehiclesList.filter(vehicle => vehicle.transmission === filterForm.transmission);
    }

    if (filterForm.price[0] || filterForm.price[1]) {
        filteredVehiclesList = filteredVehiclesList.filter(vehicle => vehicle.price >= filterForm.price[0] && vehicle.price <= filterForm.price[1]);
    }

    if (filterForm.construction_year[0] || filterForm.construction_year[1]) {
        filteredVehiclesList = filteredVehiclesList.filter(vehicle => vehicle.year >= filterForm.construction_year[0] && vehicle.year <= filterForm.construction_year[1]);
    }

    if (filterForm.mileage[0] || filterForm.mileage[1]) {
        filteredVehiclesList = filteredVehiclesList.filter(vehicle => vehicle.mileage >= filterForm.mileage[0] && vehicle.mileage <= filterForm.mileage[1]);
    }

    if (filterForm.accessories.length) {
        filteredVehiclesList = filteredVehiclesList.filter(vehicle => filterForm.accessories.every(accessory => vehicle.accessories.includes(accessory)));
    }

    if (filterForm.exterior_color.length) {
        filteredVehiclesList = filteredVehiclesList.filter(vehicle => filterForm.exterior_color.includes(vehicle.color.charAt(0).toUpperCase() + vehicle.color.slice(1)));
    }

    if (filterForm.interior_color.length) {
        filteredVehiclesList = filteredVehiclesList.filter(vehicle => filterForm.interior_color.includes(vehicle.interior_color));
    }

    if (filterForm.upholstery.length) {
        filteredVehiclesList = filteredVehiclesList.filter(vehicle => filterForm.upholstery.includes(vehicle.upholstery));
    }

    filteredVehicles.value = filteredVehiclesList;
    vehicles.value = filteredVehiclesList;
    loadedVehicles.value = vehicles.value.length;

    let filterParams = {
        brand: filterForm.brand,
        model: filterForm.model,
        type: filterForm.type,
        body: filterForm.body,
        fuel: filterForm.fuel,
        transmission: filterForm.transmission,
        price: filterForm.price,
        construction_year: filterForm.construction_year,
        mileage: filterForm.mileage,
        accessories: filterForm.accessories,
        exterior_color: filterForm.exterior_color,
        interior_color: filterForm.interior_color,
        upholstery: filterForm.upholstery,
    }

    localStorage.setItem('filterParams', JSON.stringify(filterParams));

    
    filterChanged.value = filterChangeCheck();
}

function filterChangeCheck() {
    const storedParams = JSON.parse(localStorage.getItem('filterParams'));

    if (!storedParams) return false;

    //check if the stored params are the same as the default values
    if (storedParams.brand !== null) return true;
    if (storedParams.model !== null) return true;
    if (storedParams.type !== null) return true;
    if (storedParams.body !== null) return true;
    if (storedParams.fuel !== null) return true;
    if (storedParams.transmission !== null) return true;
    if (storedParams.price[0] !== null || storedParams.price[1] !== null) return true;
    if (storedParams.construction_year[0] !== null || storedParams.construction_year[1] !== null) return true;
    if (storedParams.mileage[0] !== null || storedParams.mileage[1] !== null) return true;
    if (storedParams.accessories.length > 0) return true;
    if (storedParams.exterior_color.length > 0) return true;
    if (storedParams.interior_color.length > 0) return true;
    if (storedParams.upholstery.length > 0) return true;
    
    return false;
}

const constructionYears = ref(
    Array.from({ length: maxYear - minYear + 1 }, (_, i) => minYear + i).map(year => ({ id: year, label: year }))
);

const mileages = ref(
    Array.from({ length: 400000 / 10000 }, (_, i) => i * 10000).map(mileage => ({ id: mileage, label: mileage.toLocaleString() + ' km' }))
);

const prices = ref(
    Array.from({ length: 150000 / 10000 }, (_, i) => i * 10000).map(price => ({ id: price, label: eurFormatter.format(price) }))
);

function clearParams() {
    filterForm.brand = null;
    filterForm.model = null;
    filterForm.type = null;
    filterForm.body = null;
    filterForm.fuel = null;
    filterForm.transmission = null;
    filterForm.price = [null, null];
    filterForm.construction_year = [null, null];
    filterForm.mileage = [null, null];
    filterForm.accessories = [];
    filterForm.exterior_color = [];
    filterForm.interior_color = [];
    filterForm.upholstery = [];

    modelOptions.value = [];
    typeOptions.value = [];
}

onMounted(() => {
    const storedParams = JSON.parse(localStorage.getItem('filterParams'));

    if (storedParams) {
        if (storedParams.brand) filterForm.brand = storedParams.brand;
        if (storedParams.model) {
            setTimeout(() => {
                filterForm.model = storedParams.model;
            }, 100);
        }
        if (storedParams.type) {
            setTimeout(() => {
                filterForm.type = storedParams.type;
            }, 100);
        }
        if (storedParams.body) filterForm.body = storedParams.body;
        if (storedParams.fuel) filterForm.fuel = storedParams.fuel;
        if (storedParams.transmission) filterForm.transmission = storedParams.transmission;
        if (storedParams.price) filterForm.price = storedParams.price;
        if (storedParams.construction_year) filterForm.construction_year = storedParams.construction_year;
        if (storedParams.mileage) filterForm.mileage = storedParams.mileage;
        if (storedParams.accessories) filterForm.accessories = storedParams.accessories;
        if (storedParams.exterior_color) filterForm.exterior_color = storedParams.exterior_color;
        if (storedParams.interior_color) filterForm.interior_color = storedParams.interior_color;
        if (storedParams.upholstery) filterForm.upholstery = storedParams.upholstery;
    }

    filter();

    filterChanged.value = filterChangeCheck();
});

const accessoiresOptions = ref([...new Set(props.vehicles.map(vehicle => vehicle.accessories).flat().filter(Boolean))].map(accessoire => ({ name: accessoire })));
const exteriorColorOptions = ref([...new Set(props.vehicles.map(vehicle => vehicle.color).filter(Boolean))].map(color => ({ name: color.charAt(0).toUpperCase() + color.slice(1) })));
const interiorColorsOptions = ref([...new Set(props.vehicles.map(vehicle => vehicle.interior_color).filter(Boolean))].map(color => ({ name: color })));
const interiorMaterialsOptions = ref([...new Set(props.vehicles.map(vehicle => vehicle.upholstery).filter(Boolean))].map(material => ({ name: material })));
</script>

<template>

    <Head>
        <title>Auto Aanbod - Gebruikte Auto's bij Zwagers Automotive</title>
        <meta name="description" content="Bekijk het actuele aanbod van gebruikte auto's bij Zwagers Automotive. Kies uit een breed assortiment aan betrouwbare en hoogwaardige occasions, zorgvuldig geselecteerd op kwaliteit en kilometerstand.">
        <meta name="robots" content="index, follow" />

        <!-- Open Graph voor social media weergave -->
        <meta property="og:title" content="Actueel Aanbod - Zwagers Automotive" />
        <meta property="og:description" content="Actueel Aanbod Zwagers Automotive: Ontdek een breed aanbod betrouwbare occasions, uitstekende service en persoonlijk advies. Vind de perfecte auto die past bij jouw wensen en budget." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="/aanbod" />
        <meta property="og:image" content="/img/showroom/zwagers_showroom_35.jpg" />
        <!-- Zorg dat de afbeelding een representatieve foto van het aanbod toont -->

        <!-- Twitter Cards voor betere zichtbaarheid op Twitter -->
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Actueel Aanbod - Zwagers Automotive" />
        <meta name="twitter:description" content="Actueel Aanbod Zwagers Automotive: Ontdek een breed aanbod betrouwbare occasions, uitstekende service en persoonlijk advies. Vind de perfecte auto die past bij jouw wensen en budget." />
        <meta name="twitter:image" content="/img/showroom/zwagers_showroom_35.jpg" />
    </Head>

    <main class="flex justify-center">
        <div class="w-11/12 px:0 md:px-6 xl:px-0 max-w-6xl space-y-5 mt-20 md:mt-24 lg:mt-28 mb-10">
            <!--filters top-->
            <div class="w-full flex flex-row gap-5 items-center justify-between">
                <div class="flex flex-row gap-4">
                    <button type="button" class="button-animation primary-button !max-h-10 text-gray-700" @click="mobileFiltersOpen = !mobileFiltersOpen">
                        <font-awesome-icon :icon="['fas', 'filter']" />
                        <h6>Filters</h6>
                    </button>
                </div>
                <button type="button" class="button-animation sm:flex flex-row gap-2 items-center !max-h-10 text-gray-900 hidden" @click="clearParams" v-if="filterChanged">
                    <font-awesome-icon :icon="['fas', 'filter-circle-xmark']" />
                    <p class="text-nowrap">Reset filter</p>
                </button>
                <div class="flex w-full justify-end items-center gap-2">
                    <span class="hidden sm:block font-medium">Sorteren </span>
                    <div class="lg:w-1/4">
                        <Select v-model="selectedSort" :options="sortOptions" valueName="label" valueId="id" placeholder="Sorteer op" @change="sort" class="min-w-48" />
                    </div>
                </div>
            </div>
            <div class="lg:col-span-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" v-auto-animate>
                <div v-for="vehicle in vehicles" v-if="vehicles.length > 0" :key="vehicle.id">
                        <Vehicle :vehicle="vehicle" />
                    </div>
                    <div v-else class="flex justify-center">
                        <p>Geen resultaten gevonden</p>
                    </div>
                </div>
            </div>

                <!--filters side bar-->
                <!-- <div class="w-full hidden lg:block">
                    <div class="flex flex-col text-gray-900 gap-3.5">
                        <div class="col-span-full">
                            <Select v-model="filterForm.brand" :options="brandOptions" label="Merk" valueName="label"
                                clear valueId="id" placeholder="Selecteer een merk..." />
                        </div>
                        <div class="col-span-full" v-if="filterForm.brand">
                            <Select v-model="filterForm.model" :options="modelOptions" label="Model" valueName="label"
                                clear valueId="id" placeholder="Selecteer een model..." />
                        </div>
                        <div class="col-span-full" v-if="filterForm.model">
                            <Select v-model="filterForm.type" :options="typeOptions" label="Variant" valueName="label"
                                clear valueId="id" placeholder="Selecteer een variant..." />
                        </div>
                        <div class="col-span-full">
                            <Select v-model="filterForm.body" :options="bodyTypes" label="Carrosserie" valueName="label"
                                clear valueId="id" placeholder="Selecteer een carrosserie..." />
                        </div>
                        <div class="col-span-full">
                            <Select v-model="filterForm.fuel" :options="fuelTypes" label="Brandstof" valueName="label"
                                clear valueId="id" placeholder="Selecteer een brandstof..." />
                        </div>
                        <div class="col-span-full">
                            <Select v-model="filterForm.transmission" :options="transmissionTypes" label="Transmissie"
                                valueName="label" clear valueId="id" placeholder="Selecteer een transmissie..." />
                        </div>
                        <div class="col-span-full">
                            <label for="price" class="block text-sm font-semibold text-gray-900">Prijs</label>
                            <div class="flex gap-2 mt-2">
                                <Select :options="prices" valueName="label" valueId="id" v-model="filterForm.price[0]"
                                    placeholder="Van" class="w-1/2" />
                                <Select :options="prices" valueName="label" valueId="id" v-model="filterForm.price[1]"
                                    placeholder="Tot" class="w-1/2" />
                            </div>
                        </div>
                        <div class="col-span-full">
                            <label for="construction_year"
                                class="block text-sm font-semibold text-gray-900">Bouwjaar</label>
                            <div class="flex gap-2 mt-2">
                                <Select :options="constructionYears" valueName="label" valueId="id"
                                    v-model="filterForm.construction_year[0]" placeholder="Van" class="w-1/2" />
                                <Select :options="constructionYears" valueName="label" valueId="id"
                                    v-model="filterForm.construction_year[1]" placeholder="Tot" class="w-1/2" />
                            </div>
                        </div>
                        <div class="col-span-full">
                            <label for="mileage"
                                class="block text-sm font-semibold text-gray-900">Kilometerstand</label>
                            <div class="flex gap-2 mt-2">
                                <Select :options="mileages" valueName="label" valueId="id"
                                    v-model="filterForm.mileage[0]" placeholder="Van" class="w-1/2" />
                                <Select :options="mileages" valueName="label" valueId="id"
                                    v-model="filterForm.mileage[1]" placeholder="Tot" class="w-1/2" />
                            </div>
                        </div>
                        <hr />
                        <Disclosure v-slot="{ open }">
                            <DisclosureButton
                                class="h-9 col-span-full w-full flex items-center focus:ring-0  justify-between px-4 py-2 border border-gray-300 outline-none rounded-md shadow-sm text-sm font-medium text-black bg-white duration-200">
                                <span>Opties & accessoires</span>
                                <font-awesome-icon :icon="['far', 'chevron-down']"
                                    class="h-4 w-4 transition-transform duration-300 ease-in-out"
                                    :class="open ? '-rotate-180' : ''" aria-hidden="true" />
                            </DisclosureButton>
                            <transition enter-active-class="transition duration-100 ease-out"
                                enter-from-class="transform scale-95 opacity-0"
                                enter-to-class="transform scale-100 opacity-100"
                                leave-active-class="transition duration-75 ease-out"
                                leave-from-class="transform scale-100 opacity-100"
                                leave-to-class="transform scale-95 opacity-0">
                                <DisclosurePanel class="col-span-full">
                                    <div v-for="(accessoiresOption, index) in accessoiresOptions" :key="index"
                                        class="flex align-items-center">
                                        <Checkbox v-model="filterForm.accessories" name="category"
                                            :value="accessoiresOption.name" :label="accessoiresOption.name"
                                            :inputId="'acc_' + accessoiresOption.name" />
                                    </div>
                                    <hr class="mt-2.5" />
                                </DisclosurePanel>
                            </transition>
                        </Disclosure>
                        <Disclosure v-slot="{ open }">
                            <DisclosureButton
                                class="h-9 col-span-full w-full flex items-center focus:ring-0  justify-between px-4 py-2 border border-gray-300 outline-none rounded-md shadow-sm text-sm font-medium text-black bg-white duration-200">
                                <span>Kleur</span>
                                <font-awesome-icon :icon="['far', 'chevron-down']"
                                    class="h-4 w-4 transition-transform duration-300 ease-in-out"
                                    :class="open ? '-rotate-180' : ''" aria-hidden="true" />
                            </DisclosureButton>
                            <transition enter-active-class="transition duration-100 ease-out"
                                enter-from-class="transform scale-95 opacity-0"
                                enter-to-class="transform scale-100 opacity-100"
                                leave-active-class="transition duration-75 ease-out"
                                leave-from-class="transform scale-100 opacity-100"
                                leave-to-class="transform scale-95 opacity-0">
                                <DisclosurePanel class="col-span-full">
                                    <div v-for="(exteriorColorOption, index) in exteriorColorOptions" :key="index"
                                        class="flex align-items-center">
                                        <Checkbox v-model="filterForm.exterior_color" name="category"
                                            :value="exteriorColorOption.name" :label="exteriorColorOption.name"
                                            :inputId="'ext_color_' + exteriorColorOption.name" />
                                    </div>
                                    <hr class="mt-2.5" />
                                </DisclosurePanel>
                            </transition>
                        </Disclosure>
                        <Disclosure v-slot="{ open }">
                            <DisclosureButton
                                class="h-9 col-span-full w-full flex items-center focus:ring-0  justify-between px-4 py-2 border border-gray-300 outline-none rounded-md shadow-sm text-sm font-medium text-black bg-white duration-200">
                                <span>Interieur</span>
                                <font-awesome-icon :icon="['far', 'chevron-down']"
                                    class="h-4 w-4 transition-transform duration-300 ease-in-out"
                                    :class="open ? '-rotate-180' : ''" aria-hidden="true" />
                            </DisclosureButton>
                            <transition enter-active-class="transition duration-100 ease-out"
                                enter-from-class="transform scale-95 opacity-0"
                                enter-to-class="transform scale-100 opacity-100"
                                leave-active-class="transition duration-75 ease-out"
                                leave-from-class="transform scale-100 opacity-100"
                                leave-to-class="transform scale-95 opacity-0">
                                <DisclosurePanel class="col-span-full">
                                    <div v-for="(interiorMaterialOption, index) in interiorMaterialsOptions"
                                        :key="index" class="flex align-items-center">
                                        <Checkbox v-model="filterForm.upholstery" name="category"
                                            :value="interiorMaterialOption.name" :label="interiorMaterialOption.name"
                                            :inputId="'upholstery_' + interiorMaterialOption.name" />
                                    </div>
                                    <hr class="mt-2.5" />
                                </DisclosurePanel>
                            </transition>

                        </Disclosure>
                        <Disclosure v-slot="{ open }">
                            <DisclosureButton
                                class="h-9 col-span-full w-full flex items-center focus:ring-0  justify-between px-4 py-2 border border-gray-300 outline-none rounded-md shadow-sm text-sm font-medium text-black bg-white duration-200">
                                <span>Interieur kleur</span>
                                <font-awesome-icon :icon="['far', 'chevron-down']"
                                    class="h-4 w-4 transition-transform duration-300 ease-in-out"
                                    :class="open ? '-rotate-180' : ''" aria-hidden="true" />
                            </DisclosureButton>
                            <transition enter-active-class="transition duration-100 ease-out"
                                enter-from-class="transform scale-95 opacity-0"
                                enter-to-class="transform scale-100 opacity-100"
                                leave-active-class="transition duration-75 ease-out"
                                leave-from-class="transform scale-100 opacity-100"
                                leave-to-class="transform scale-95 opacity-0">
                                <DisclosurePanel class="col-span-full">
                                    <div v-for="(interiorColorOption, index) in interiorColorsOptions" :key="index"
                                        class="flex align-items-center">
                                        <Checkbox v-model="filterForm.interior_color" name="category"
                                            :value="interiorColorOption.name" :label="interiorColorOption.name"
                                            :inputId="'int_color_' + interiorColorOption.name" />
                                    </div>
                                    <hr class="mt-2.5" />
                                </DisclosurePanel>
                            </transition>
                        </Disclosure>
                    </div>
                </div> -->

    </main>

    <TransitionRoot as="template" :show="mobileFiltersOpen">
        <Dialog class="relative z-50" @close="mobileFiltersOpen = false">
            <TransitionChild as="template" enter="transition-opacity ease-linear duration-300" enter-from="opacity-0"
                enter-to="opacity-100" leave="transition-opacity ease-linear duration-300" leave-from="opacity-100"
                leave-to="opacity-0">
                <div class="fixed inset-0 bg-black/25" />
            </TransitionChild>

            <div class="fixed inset-0 z-40 flex">
                <TransitionChild as="template" enter="transition ease-in-out duration-300 transform"
                    enter-from="translate-x-full" enter-to="translate-x-0"
                    leave="transition ease-in-out duration-300 transform" leave-from="translate-x-0"
                    leave-to="translate-x-full">
                    <DialogPanel
                        class="relative ml-auto flex w-full sm:max-w-xs md:max-w-sm lg:max-w-md flex-col overflow-y-auto bg-white py-4 shadow-xl p-5">
                        <!-- Filters -->
                        <div class="flex justify-between items-center pb-2">
                            <div class="flex flex-row items-center gap-2">
                                <h6>Filters</h6>
                            </div>
                            <button type="button" class="button-animation flex flex-row gap-2 items-center !max-h-10 text-gray-900" @click="clearParams" v-if="filterChanged">
                                <font-awesome-icon :icon="['fas', 'filter-circle-xmark']" />
                                <p class="text-nowrap">Reset filter</p>
                            </button>
                        </div>
                        <hr />
                        <div class="mt-3 grid sm:grid-cols-12 text-sm font-normal text-gray-900 gap-5">
                            <div class="col-span-full">
                                <Select v-model="filterForm.brand" :options="brandOptions" label="Merk" valueName="label" clear valueId="id" placeholder="Selecteer een merk..." />
                            </div>
                            <div class="col-span-full" v-if="filterForm.brand">
                                <Select v-model="filterForm.model" :options="modelOptions" label="Model" valueName="label" clear valueId="id" placeholder="Selecteer een model..." />
                            </div>
                            <div class="col-span-full" v-if="filterForm.model">
                                <Select v-model="filterForm.type" :options="typeOptions" label="Type" valueName="label" clear valueId="id" placeholder="Selecteer een type..." />
                            </div>
                            <div class="col-span-full">
                                <Select v-model="filterForm.body" :options="bodyTypes" label="Carrosserie" valueName="label" clear valueId="id" placeholder="Selecteer een carrosserie..." />
                            </div>
                            <div class="col-span-full">
                                <Select v-model="filterForm.fuel" :options="fuelTypes" label="Brandstof" valueName="label" clear valueId="id" placeholder="Selecteer een brandstof..." />
                            </div>
                            <div class="col-span-full">
                                <Select v-model="filterForm.transmission" :options="transmissionTypes" label="Transmissie" valueName="label" clear valueId="id" placeholder="Selecteer een transmissie..." />
                            </div>
                            <div class="col-span-full">
                                <label for="price" class="block text-sm font-semibold text-gray-900">Prijs</label>
                                <div class="flex gap-2 mt-2">
                                    <Select :options="prices" valueName="label" valueId="id" v-model="filterForm.price[0]" placeholder="Van" class="w-1/2" />
                                    <Select :options="prices" valueName="label" valueId="id"
                                        v-model="filterForm.price[1]" placeholder="Tot" class="w-1/2" />
                                </div>
                            </div>
                            <div class="col-span-full">
                                <label for="construction_year"
                                    class="block text-sm font-semibold text-gray-900">Bouwjaar</label>
                                <div class="flex gap-2 mt-2">
                                    <Select :options="constructionYears" valueName="label" valueId="id"
                                        v-model="filterForm.construction_year[0]" placeholder="Van" class="w-1/2" />
                                    <Select :options="constructionYears" valueName="label" valueId="id"
                                        v-model="filterForm.construction_year[1]" placeholder="Tot" class="w-1/2" />
                                </div>
                            </div>
                            <div class="col-span-full">
                                <label for="mileage"
                                    class="block text-sm font-semibold text-gray-900">Kilometerstand</label>
                                <div class="flex gap-2 mt-2">
                                    <Select :options="mileages" valueName="label" valueId="id"
                                        v-model="filterForm.mileage[0]" placeholder="Van" class="w-1/2" />
                                    <Select :options="mileages" valueName="label" valueId="id"
                                        v-model="filterForm.mileage[1]" placeholder="Tot" class="w-1/2" />
                                </div>
                                <hr class="mt-3.5" />
                            </div>

                            <Disclosure v-slot="{ open }">
                                <DisclosureButton
                                    class="h-9 col-span-full w-full flex items-center focus:ring-0  justify-between px-4 py-2 border border-gray-300 outline-none rounded-md shadow-sm text-sm font-medium text-black bg-white duration-200">
                                    <span>Opties & accessoires</span>
                                    <font-awesome-icon :icon="['far', 'chevron-down']"
                                        class="h-4 w-4 transition-transform duration-300 ease-in-out"
                                        :class="open ? '-rotate-180' : ''" aria-hidden="true" />
                                </DisclosureButton>
                                <transition enter-active-class="transition duration-100 ease-out"
                                    enter-from-class="transform scale-95 opacity-0"
                                    enter-to-class="transform scale-100 opacity-100"
                                    leave-active-class="transition duration-75 ease-out"
                                    leave-from-class="transform scale-100 opacity-100"
                                    leave-to-class="transform scale-95 opacity-0">
                                    <DisclosurePanel class="col-span-full">
                                        <div v-for="(accessoiresOption, index) in accessoiresOptions" :key="index"
                                            class="flex align-items-center">
                                            <Checkbox v-model="filterForm.accessories" name="category"
                                                :value="accessoiresOption.name" :label="accessoiresOption.name"
                                                :inputId="'acc_' + accessoiresOption.name" />
                                        </div>
                                        <hr class="mt-2.5" />
                                    </DisclosurePanel>
                                </transition>
                            </Disclosure>
                            <Disclosure v-slot="{ open }">
                                <DisclosureButton
                                    class="h-9 col-span-full w-full flex items-center focus:ring-0  justify-between px-4 py-2 border border-gray-300 outline-none rounded-md shadow-sm text-sm font-medium text-black bg-white duration-200">
                                    <span>Kleur</span>
                                    <font-awesome-icon :icon="['far', 'chevron-down']"
                                        class="h-4 w-4 transition-transform duration-300 ease-in-out"
                                        :class="open ? '-rotate-180' : ''" aria-hidden="true" />
                                </DisclosureButton>
                                <transition enter-active-class="transition duration-100 ease-out"
                                    enter-from-class="transform scale-95 opacity-0"
                                    enter-to-class="transform scale-100 opacity-100"
                                    leave-active-class="transition duration-75 ease-out"
                                    leave-from-class="transform scale-100 opacity-100"
                                    leave-to-class="transform scale-95 opacity-0">
                                    <DisclosurePanel class="col-span-full">
                                        <div v-for="(exteriorColorOption, index) in exteriorColorOptions" :key="index"
                                            class="flex align-items-center">
                                            <Checkbox v-model="filterForm.exterior_color" name="category"
                                                :value="exteriorColorOption.name" :label="exteriorColorOption.name"
                                                :inputId="'ext_color_' + exteriorColorOption.name" />
                                        </div>
                                        <hr class="mt-2.5" />
                                    </DisclosurePanel>
                                </transition>
                            </Disclosure>
                            <Disclosure v-slot="{ open }">
                                <DisclosureButton
                                    class="h-9 col-span-full w-full flex items-center focus:ring-0  justify-between px-4 py-2 border border-gray-300 outline-none rounded-md shadow-sm text-sm font-medium text-black bg-white duration-200">
                                    <span>Interieur</span>
                                    <font-awesome-icon :icon="['far', 'chevron-down']"
                                        class="h-4 w-4 transition-transform duration-300 ease-in-out"
                                        :class="open ? '-rotate-180' : ''" aria-hidden="true" />
                                </DisclosureButton>
                                <transition enter-active-class="transition duration-100 ease-out"
                                    enter-from-class="transform scale-95 opacity-0"
                                    enter-to-class="transform scale-100 opacity-100"
                                    leave-active-class="transition duration-75 ease-out"
                                    leave-from-class="transform scale-100 opacity-100"
                                    leave-to-class="transform scale-95 opacity-0">
                                    <DisclosurePanel class="col-span-full">
                                        <div v-for="(interiorMaterialOption, index) in interiorMaterialsOptions"
                                            :key="index" class="flex align-items-center">
                                            <Checkbox v-model="filterForm.upholstery" name="category"
                                                :value="interiorMaterialOption.name"
                                                :label="interiorMaterialOption.name"
                                                :inputId="'upholstery_' + interiorMaterialOption.name" />
                                        </div>
                                        <hr class="mt-2.5" />
                                    </DisclosurePanel>
                                </transition>

                            </Disclosure>
                            <Disclosure v-slot="{ open }">
                                <DisclosureButton
                                    class="h-9 col-span-full w-full flex items-center focus:ring-0  justify-between px-4 py-2 border border-gray-300 outline-none rounded-md shadow-sm text-sm font-medium text-black bg-white duration-200">
                                    <span>Interieur kleur</span>
                                    <font-awesome-icon :icon="['far', 'chevron-down']"
                                        class="h-4 w-4 transition-transform duration-300 ease-in-out"
                                        :class="open ? '-rotate-180' : ''" aria-hidden="true" />
                                </DisclosureButton>
                                <transition enter-active-class="transition duration-100 ease-out"
                                    enter-from-class="transform scale-95 opacity-0"
                                    enter-to-class="transform scale-100 opacity-100"
                                    leave-active-class="transition duration-75 ease-out"
                                    leave-from-class="transform scale-100 opacity-100"
                                    leave-to-class="transform scale-95 opacity-0">
                                    <DisclosurePanel class="col-span-full">
                                        <div v-for="(interiorColorOption, index) in interiorColorsOptions" :key="index"
                                            class="flex align-items-center">
                                            <Checkbox v-model="filterForm.interior_color" name="category"
                                                :value="interiorColorOption.name" :label="interiorColorOption.name"
                                                :inputId="'int_color_' + interiorColorOption.name" />
                                        </div>
                                        <hr class="mt-2.5" />
                                    </DisclosurePanel>
                                </transition>
                            </Disclosure>
                        </div>
                        <div class="sticky bottom-0 pt-4">
                            <Button @click="mobileFiltersOpen = false" class="primary-button sm:!w-full">Sluiten</Button>
                        </div>
                    </DialogPanel>
                </TransitionChild>
            </div>
        </Dialog>
    </TransitionRoot>
</template>
